/**
 * Sync Advisor LLM Service — Phase 4
 *
 * Uses callFeatureLlm to suggest conflict resolutions and trust-rule
 * configurations for the sync layer.
 *
 * Registry: sync_advisor — embedded advisor. Transport: callFeatureLlm.
 * Returns null gracefully when no LLM is available.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

// ── Types ─────────────────────────────────────────────────────

export interface ConflictAdvisorInput {
  /** Entity type (e.g. 'item', 'strategy', 'objective') */
  entityType: string;
  /** Field that conflicted */
  fieldName: string | null;
  /** Current priorities.ai value */
  localValue: unknown;
  /** Value from the external system */
  externalValue: unknown;
  /** Name of the external system (e.g. 'Jira', 'Linear') */
  connectorType: string;
  /** Optional context about the entity */
  entityContext?: string;
}

export interface ConflictAdvisorResult {
  /** Recommended winner */
  recommendedWinner: 'local' | 'external';
  /** How confident the model is */
  confidence: 'high' | 'medium' | 'low';
  /** Plain-language rationale (1–2 sentences) */
  rationale: string;
  /** Suggested note to attach to the resolution record */
  resolutionNote: string;
  /** Whether the model suggests setting a trust rule to prevent this in future */
  suggestTrustRule: boolean;
  /** If suggestTrustRule is true, the recommended trust rule */
  suggestedOwner?: 'priorities_ai' | 'external' | 'negotiated';
}

export interface TrustRuleAdvisorInput {
  /** The connector type */
  connectorType: string;
  /** Description of what this connector manages */
  connectorDescription?: string;
  /** Current trust rules (field → source) */
  currentRules: Record<string, string>;
  /** List of past conflicts (field + source of each) */
  conflictHistory?: Array<{ field: string; source: string; count: number }>;
}

export interface TrustRuleAdvisorResult {
  /** Suggested trust rule updates — only fields that should change */
  suggestedRules: Record<string, 'priorities_ai' | 'external' | 'negotiated'>;
  /** Plain-language explanation of the suggestions */
  rationale: string;
  /** Whether the suggestions are based on conflict history or general heuristics */
  basis: 'conflict_history' | 'heuristics';
  confidence: 'high' | 'medium' | 'low';
}

// ── System prompt ─────────────────────────────────────────────

const CONFLICT_SYSTEM_PROMPT = `You are a sync advisor for a strategic prioritization platform called priorities.ai.

Your job is to recommend which value should win when two systems disagree on a field.

## Context
- priorities.ai manages strategic prioritization: what gets worked on, in what order, in which cycle.
- External systems (Jira, Linear, GitHub, etc.) manage operational execution.
- priorities.ai is the source of: priority_rank, scope, cycle_id, strategic context.
- External systems are the source of: status, assignee, time_spent, due_date, operational metadata.
- Shared/negotiated: title, description, labels.

## Rules
- If the field is core to prioritization strategy (rank, scope, cycle) → local wins.
- If the field is purely operational state (status, assignee, time) → external wins.
- If the field is descriptive content (title, description) → whichever is more recent / more informative wins.
- Always explain your reasoning in plain English that a non-technical ops lead can understand.

## Output format
Respond ONLY with valid JSON:
{
  "recommendedWinner": "local" | "external",
  "confidence": "high" | "medium" | "low",
  "rationale": "<1-2 sentences>",
  "resolutionNote": "<short note for the audit log>",
  "suggestTrustRule": true | false,
  "suggestedOwner": "priorities_ai" | "external" | "negotiated" | null
}`;

const TRUST_RULE_SYSTEM_PROMPT = `You are a sync configuration advisor for a strategic prioritization platform called priorities.ai.

Your job is to recommend trust rules — which system is the source of each field — for a given connector integration.

## Trust rule options
- priorities_ai: priorities.ai is authoritative. External changes to this field are rejected as conflicts.
- external: The external system is authoritative. priorities.ai accepts incoming changes to this field.
- negotiated: Neither is the source outright. Conflicts are queued for human review.

## Known field source patterns
- priority_rank, scope, cycle_id → almost always priorities_ai
- status, assignee, time_spent, due_date → almost always external
- title, description, labels → negotiated (both systems may legitimately edit these)

## Output format
Respond ONLY with valid JSON:
{
  "suggestedRules": { "<field>": "priorities_ai" | "external" | "negotiated" },
  "rationale": "<2-3 sentences explaining the reasoning>",
  "basis": "conflict_history" | "heuristics",
  "confidence": "high" | "medium" | "low"
}

Only include fields in suggestedRules that should change from the current rules.`;

function parseJSON<T>(text: string | null): T | null {
  if (!text) return null;
  try {
    const match = text.match(/\{[\s\S]*\}/);
    if (match) return JSON.parse(match[0]) as T;
  } catch { /* ignore */ }
  return null;
}

// ── Public API ────────────────────────────────────────────────

/**
 * Ask the LLM to suggest which value should win for a given conflict.
 * Returns null if no LLM is configured.
 */
export async function suggestConflictResolution(
  input: ConflictAdvisorInput,
): Promise<ConflictAdvisorResult | null> {
  if (!isAiCapabilityEnabled('sync_advisor')) return null;

  const userMessage = [
    `Entity type: ${input.entityType}`,
    `Field: ${input.fieldName ?? '(whole entity)'}`,
    `priorities.ai value: ${JSON.stringify(input.localValue)}`,
    `${input.connectorType} value: ${JSON.stringify(input.externalValue)}`,
    input.entityContext ? `Context: ${input.entityContext}` : null,
    '',
    `Which value should win, and why?`,
  ].filter(Boolean).join('\n');

  const raw = await callFeatureLlm({
    userPrompt: userMessage,
    systemPrompt: CONFLICT_SYSTEM_PROMPT,
    conversationId: 'sync-advisor-conflict-' + Date.now(),
    model: DEFAULT_MODEL,
    temperature: 0.3,
    maxTokens: 400,
  });

  return parseJSON<ConflictAdvisorResult>(raw);
}

/**
 * Ask the LLM to suggest trust rule updates for a connector based on
 * past conflict history and general heuristics.
 * Returns null if no LLM is configured.
 */
export async function suggestTrustRules(
  input: TrustRuleAdvisorInput,
): Promise<TrustRuleAdvisorResult | null> {
  if (!isAiCapabilityEnabled('sync_advisor')) return null;

  const conflictSummary = input.conflictHistory
    ?.map((c) => `  - "${c.field}" conflicted ${c.count}x (source: ${c.source})`)
    .join('\n');

  const userMessage = [
    `Connector: ${input.connectorType}`,
    input.connectorDescription ? `Description: ${input.connectorDescription}` : null,
    '',
    `Current trust rules:`,
    Object.entries(input.currentRules)
      .map(([field, source]) => `  ${field}: ${source}`)
      .join('\n') || '  (none configured)',
    '',
    conflictSummary ? `Recent conflict history:\n${conflictSummary}` : 'No recent conflict history.',
    '',
    'What trust rule changes would you recommend?',
  ].filter(Boolean).join('\n');

  const raw = await callFeatureLlm({
    userPrompt: userMessage,
    systemPrompt: TRUST_RULE_SYSTEM_PROMPT,
    conversationId: 'sync-advisor-trust-' + Date.now(),
    model: DEFAULT_MODEL,
    temperature: 0.3,
    maxTokens: 400,
  });

  return parseJSON<TrustRuleAdvisorResult>(raw);
}
